import {
    AddCircleOutline,
    BlockOutlined,
    CheckCircleOutline,
    CloseOutlined,
    DeleteOutlineOutlined,
    PersonOutlineOutlined,
    ShieldOutlined,
    UndoOutlined,
} from '@mui/icons-material';
import {Alert, Box, Grid, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {
    useBindUserMutation,
    useGetEffectivePermissionsQuery,
    useGetPermissionCatalogQuery,
    useGetRolesQuery,
    useGetUserBindingQuery,
    useReplaceOverridesMutation,
    usersApi,
} from 'src/api';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {PageHeader} from 'src/components/common/PageHeader';
import {PermissionPickerModal} from 'src/components/iam/PermissionPickerModal';
import {IAM_TONE, calloutSx, listRowSx, pillSx, subjectBadgeSx, subjectBandSx} from 'src/components/iam/iam.sx';
import {KpiCard} from 'src/pages/EnergyConsumptionReport/components/KpiCard';
import {useCan, useScope} from 'src/permissions/hooks';
import {BaseUserInfo, PermissionDecisionDTO, PermissionOverride} from 'src/types';
import {getEvDriverNameFromBaseUser} from 'src/utils/userNameFormatter';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import theme from 'src/theme';
import {cardSx} from '../AccessControl/AccessControl.sx';
import {useBusinessId} from 'src/app/hooks';

/** Why a permission ended up allowed, in the order the server decides them. */
const REASON_LABELS: Record<string, string> = {
    ROLE_GRANT: 'accessControlReasonRole',
    GROUP_GRANT: 'accessControlReasonGroup',
    USER_ALLOW: 'accessControlReasonUserAllow',
    USER_DENY: 'accessControlReasonUserDeny',
    OUTSIDE_BOUNDARY: 'accessControlReasonBoundary',
    NO_GRANT: 'accessControlReasonNoGrant',
};

/**
 * One person's access: the role they hold, the exceptions made for them, and the result.
 *
 * The two kinds of exception get their own labelled card rather than sharing a list, because they are
 * opposites and the old shared list needed a dropdown to tell them apart. Which one you are making is
 * never chosen here: adding happens from a dialog offering only what the person lacks, taking away
 * happens on the permission itself. Everything a role does not grant is already refused, so a refusal
 * is only ever meaningful as "take this away from them specifically".
 */
export const UserAccess: FC = () => {
    const intl = useIntl();
    const params = useParams();
    const userId = Number(params.userId);

    const canBind = useCan('accessControl.manageBindings');
    const canOverride = useCan('accessControl.manageOverrides');

    // The endpoint still takes a businessId; the server overwrites it with the caller's own scope, so
    // sending anything else would be pointless rather than dangerous. Sending ours keeps them equal.
    const scope = useScope();
    // Roles belong to a business, so the list follows the one being worked in.
    const businessId = useBusinessId();
    const {data: user} = usersApi.useGetUserByIdQuery(
        {userId, businessId: scope?.businessId as number},
        {skip: !userId}
    );
    const {data: roles} = useGetRolesQuery(businessId);
    const {data: catalog} = useGetPermissionCatalogQuery();
    const {data: decisions} = useGetEffectivePermissionsQuery(userId, {skip: !userId});
    // Read from the binding, not inferred from the decisions: a role that grants nothing produces no
    // decision naming it, and the select would sit empty beside a person who plainly has a role.
    const {data: binding} = useGetUserBindingQuery(userId, {skip: !userId});

    const [bindUser] = useBindUserMutation();
    const [replaceOverrides, {isLoading: savingGrant}] = useReplaceOverridesMutation();

    const [granting, setGranting] = useState(false);

    const allowed = useMemo(() => (decisions ?? []).filter((d) => d.allowed), [decisions]);
    const givenExtra = useMemo(
        () => (decisions ?? []).filter((d) => d.reason === 'USER_ALLOW'),
        [decisions]
    );
    const takenAway = useMemo(
        () => (decisions ?? []).filter((d) => d.reason === 'USER_DENY'),
        [decisions]
    );
    const heldRoles = useMemo(() => binding?.roles ?? [], [binding]);
    const heldRoleIds = useMemo(() => new Set(heldRoles.map((role) => role.id)), [heldRoles]);
    // What is left to add. Offering a role somebody already holds invites a click that does nothing.
    const addableRoles = useMemo(
        () => (roles ?? []).filter((role) => !heldRoleIds.has(role.id)),
        [roles, heldRoleIds]
    );

    const byGroup = useMemo(() => {
        const groups = new Map<string, PermissionDecisionDTO[]>();
        allowed.forEach((decision) => {
            const subject = decision.permission.split('.')[0];
            const list = groups.get(subject) ?? [];
            list.push(decision);
            groups.set(subject, list);
        });
        return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [allowed]);

    const held = useMemo(() => new Set(allowed.map((d) => d.permission)), [allowed]);

    /**
     * The reaches this person may be given, as the server computed them.
     *
     * Not derived here. The ceiling lives on the account type and is enforced when the binding is
     * saved; an editor that worked it out again would eventually offer something the server refuses,
     * and the person would meet an error where they expected a choice.
     */
    /**
     * How far this person sees. Shown, never chosen: it follows from the kind of account they hold,
     * and a role only says what they may do. Choosing it per role was tried and taken out — for every
     * type but one there was a single answer, and the others were ways to end up with a blank screen.
     */
    const reachLabel = binding?.scope?.type
        ? intl.formatMessage({
            id: `accessControlScope.${binding.scope.type}`,
            defaultMessage: binding.scope.type,
        })
        : '';

    const notify = (error: unknown) =>
        NotifyError(
            isErrorObject(error)
                ? error.data.message
                : intl.formatMessage({id: 'somethingWentWrong', defaultMessage: 'Something went wrong'})
        );

    /**
     * Sends the whole set every time.
     *
     * The endpoint replaces rather than adds or removes, so two people editing the same person cannot
     * end up with a result that depends on whose click arrived first.
     */
    const setRoles = async (roleIds: number[]) => {
        try {
            await bindUser({userId, data: {roleIds}}).unwrap();
            NotifySuccess(intl.formatMessage({id: 'saved', defaultMessage: 'Saved'}));
        } catch (error) {
            notify(error);
        }
    };

    const addRole = (roleId: number) => setRoles([...heldRoles.map((role) => role.id), roleId]);
    const removeRole = (roleId: number) =>
        setRoles(heldRoles.filter((role) => role.id !== roleId).map((role) => role.id));

    /**
     * The exceptions as they stand, which is what every edit starts from.
     *
     * Read off the decisions rather than kept in state: the server has just told us what it decided,
     * and a second copy here would be one more thing able to disagree with it.
     */
    const exceptions = useMemo<PermissionOverride[]>(
        () => [
            ...givenExtra.map((d) => ({permission: d.permission, effect: 'ALLOW' as const})),
            ...takenAway.map((d) => ({permission: d.permission, effect: 'DENY' as const})),
        ].map(({permission, effect}) => ({permissionName: permission, effect})),
        [givenExtra, takenAway]
    );

    /**
     * Writes the whole set of exceptions.
     *
     * One request whatever the size of the edit. It used to be one per permission - granting a subject
     * of twenty produced twenty saves, announced twenty times, any of which could fail and leave an
     * access state nobody had chosen.
     */
    const writeExceptions = async (next: PermissionOverride[]) => {
        try {
            await replaceOverrides({userId, data: {overrides: next}}).unwrap();
            NotifySuccess(intl.formatMessage({id: 'saved', defaultMessage: 'Saved'}));
            return true;
        } catch (error) {
            notify(error);
            return false;
        }
    };

    /** Everything currently excepted, minus these permissions - the base for any change to them. */
    const without = (names: Set<string>) =>
        exceptions.filter((override) => !names.has(override.permissionName));

    /**
     * One endpoint, two verbs, and which one is in play is never a question the person answers: the
     * dialog only ever grants, the row control only ever takes away.
     */
    const setException = (permissionName: string, effect: 'ALLOW' | 'DENY') =>
        writeExceptions([...without(new Set([permissionName])), {permissionName, effect}]);

    /**
     * Take a whole subject away in one go.
     *
     * "This person has no business touching invoices" is one decision, and making it six times over is
     * six chances to stop halfway and leave a half-refused subject nobody can read the intent of.
     *
     * Each pill keeps its own verb: an extra given by hand is withdrawn, anything a role grants is
     * refused. Mixing them into a single "deny everything" would leave USER_DENY rows for permissions
     * the person never had, which is the state the refusals section exists to warn about.
     *
     * Concurrent rather than sequential: they are independent writes, and one round trip per pill was
     * long enough to watch happen.
     */
    const removeWholeSubject = (group: PermissionDecisionDTO[]) => {
        const touched = new Set(group.map((decision) => decision.permission));
        const refusals = group
            // An extra given by hand is simply withdrawn. Only what a role grants has to be refused,
            // and refusing the rest would leave DENY rows for permissions the person never had - the
            // state the refusals section exists to warn about.
            .filter((decision) => decision.reason !== 'USER_ALLOW')
            .map((decision) => ({permissionName: decision.permission, effect: 'DENY' as const}));
        return writeExceptions([...without(touched), ...refusals]);
    };

    const clearException = (permissionName: string) =>
        writeExceptions(without(new Set([permissionName])));

    // The shared formatter, so a company's account reads as the company here too rather than
    // as a blank pair of missing name fields.
    const fullName = user ? getEvDriverNameFromBaseUser(user as unknown as BaseUserInfo) : '';

    return (
        <Box sx={{pb: 6}}>
            <PageHeader
                title={fullName}
                icon={<PersonOutlineOutlined/>}
                onlyTitle={!canOverride}
                buttonText="accessControlGrantExtra"
                onClick={() => setGranting(true)}
            />
            {user && (
                <Typography sx={{fontSize: '0.8125rem', color: theme.palette.neutral[400]}}>
                    {user.emailAddress}
                </Typography>
            )}

            <Grid container spacing={2} sx={{mt: 0.5}}>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlEffective',
                            defaultMessage: 'What they can do',
                        })}
                        value={String(allowed.length)}
                        unit={intl.formatMessage({
                            id: 'accessControlPermissionsWord',
                            defaultMessage: 'permissions',
                        })}
                        icon={<CheckCircleOutline fontSize="small"/>}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlGivenExtra',
                            defaultMessage: 'Given extra',
                        })}
                        value={String(givenExtra.length)}
                        unit={intl.formatMessage({
                            id: 'accessControlOnTopOfRole',
                            defaultMessage: 'on top of their role',
                        })}
                        icon={<AddCircleOutline fontSize="small"/>}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlTakenAway',
                            defaultMessage: 'Taken away',
                        })}
                        value={String(takenAway.length)}
                        unit={intl.formatMessage({
                            id: 'accessControlDespiteRole',
                            defaultMessage: 'despite their role',
                        })}
                        icon={<BlockOutlined fontSize="small"/>}
                    />
                </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{mt: 3, mb: 1, fontWeight: 600}}>
                <FormattedMessage id="accessControlRole" defaultMessage="Roles"/>
            </Typography>
            <Typography sx={{mb: 1.5, fontSize: '0.875rem', color: theme.palette.grey[500], maxWidth: 760}}>
                <FormattedMessage
                    id="accessControlRoleHint"
                    defaultMessage="Everything this person can do is the sum of their roles, unless an exception below says otherwise. Each role reaches as far as it is given here — the same role can cover a whole business for one person and only their own records for another."
                />
            </Typography>

            {binding && heldRoles.length === 0 ? (
                <Alert severity="warning" sx={{borderRadius: '14px', mb: 2}}>
                    <FormattedMessage
                        id="accessControlRoleUnknown"
                        defaultMessage="No roles are assigned, so no permission is reaching this person."
                    />
                </Alert>
            ) : null}

            {/* The reach, once, as a statement about the person. Every role they hold reaches this
                far and no further, so repeating it on each row would only invite the question of what
                to do when two disagree - which is a question this model does not have. */}
            {reachLabel && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
                    <Typography sx={{fontSize: '0.875rem', color: theme.palette.grey[500]}}>
                        <FormattedMessage id="accessControlReach" defaultMessage="Reach"/>
                    </Typography>
                    <Typography sx={pillSx(IAM_TONE.none)}>{reachLabel}</Typography>
                </Stack>
            )}

            {heldRoles.length > 0 && (
                <Box sx={{...cardSx, mb: 2, maxWidth: 460}}>
                    {heldRoles.map((role) => (
                        <Box key={role.id} sx={listRowSx}>
                            <Box sx={subjectBadgeSx}>
                                <ShieldOutlined fontSize="small"/>
                            </Box>
                            <Typography sx={{fontWeight: 600, fontSize: '0.9375rem', flex: 1, minWidth: 0}}>
                                {role.name}
                            </Typography>
                            {canBind && (
                                <Tooltip title={intl.formatMessage({
                                    id: 'accessControlRemoveRole', defaultMessage: 'Remove this role',
                                })}>
                                    <IconButton size="small" onClick={() => removeRole(role.id)}>
                                        <DeleteOutlineOutlined fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {canBind && addableRoles.length > 0 && (
                <Box sx={{maxWidth: 360}}>
                    <AMPFormSelect
                        input="role"
                        label="accessControlAddRole"
                        value=""
                        onChange={(event) => addRole(Number(event.target.value))}
                        data={addableRoles.map((role) => ({
                            value: role.id,
                            id: role.id,
                            name: role.name,
                        }))}
                    />
                </Box>
            )}

            <Box sx={calloutSx(IAM_TONE.granted)}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AddCircleOutline sx={{color: IAM_TONE.granted.fg}}/>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                        <FormattedMessage id="accessControlGivenExtra" defaultMessage="Given extra"/>
                    </Typography>
                    <Typography sx={{...pillSx(IAM_TONE.granted), ml: 'auto'}}>
                        {givenExtra.length}
                    </Typography>
                </Stack>
                <Typography sx={{mt: 0.5, fontSize: '0.875rem', color: theme.palette.grey[500]}}>
                    <FormattedMessage
                        id="accessControlGivenExtraHint"
                        defaultMessage="Granted to this person on top of their role."
                    />
                </Typography>
            </Box>

            <Box sx={cardSx}>
                {givenExtra.length === 0 ? (
                    <Typography sx={{px: 2.5, py: 3, fontSize: '0.875rem', color: theme.palette.grey[500]}}>
                        <FormattedMessage
                            id="accessControlNoOverrides"
                            defaultMessage="None — this person gets exactly what their role grants."
                        />
                    </Typography>
                ) : (
                    givenExtra.map((decision) => (
                        <Box key={decision.permission} sx={listRowSx}>
                            <AddCircleOutline sx={{fontSize: 18, color: IAM_TONE.granted.fg}}/>
                            <Typography variant="body2" sx={{flex: 1, minWidth: 0, fontWeight: 500}}>
                                {decision.permission}
                            </Typography>
                            {canOverride && (
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: 'accessControlRemoveExtra',
                                        defaultMessage: 'Remove this extra permission',
                                    })}
                                >
                                    <IconButton
                                        className="rowAction"
                                        size="small"
                                        onClick={() => clearException(decision.permission)}
                                    >
                                        <CloseOutlined fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    ))
                )}
            </Box>

            <Box sx={calloutSx(IAM_TONE.refused)}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <BlockOutlined sx={{color: IAM_TONE.refused.fg}}/>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                        <FormattedMessage id="accessControlTakenAway" defaultMessage="Taken away"/>
                    </Typography>
                    <Typography sx={{...pillSx(IAM_TONE.refused), ml: 'auto'}}>
                        {takenAway.length}
                    </Typography>
                </Stack>
                <Typography sx={{mt: 0.5, fontSize: '0.875rem', color: theme.palette.grey[500]}}>
                    <FormattedMessage
                        id="accessControlTakenAwayHint"
                        defaultMessage="Refused to this person even though their role grants it."
                    />
                </Typography>
            </Box>

            <Box sx={cardSx}>
                {takenAway.length === 0 ? (
                    <Typography sx={{px: 2.5, py: 3, fontSize: '0.875rem', color: theme.palette.grey[500]}}>
                        <FormattedMessage
                            id="accessControlDenyExplained"
                            defaultMessage="Nothing. You never need to refuse something explicitly — anything a role does not grant is already refused. A refusal here only exists to take something away from a role."
                        />
                    </Typography>
                ) : (
                    takenAway.map((decision) => (
                        <Box key={decision.permission} sx={listRowSx}>
                            <BlockOutlined sx={{fontSize: 18, color: IAM_TONE.refused.fg}}/>
                            <Typography
                                variant="body2"
                                sx={{flex: 1, minWidth: 0, fontWeight: 500, textDecoration: 'line-through'}}
                            >
                                {decision.permission}
                            </Typography>
                            {canOverride && (
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: 'accessControlGiveBack',
                                        defaultMessage: 'Give it back',
                                    })}
                                >
                                    <IconButton
                                        className="rowAction"
                                        size="small"
                                        onClick={() => clearException(decision.permission)}
                                    >
                                        <UndoOutlined fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    ))
                )}
            </Box>

            <Typography variant="subtitle1" sx={{mt: 3, mb: 1.5, fontWeight: 600}}>
                <FormattedMessage id="accessControlEffective" defaultMessage="What they can do"/>{' '}
                <Typography component="span" sx={pillSx(allowed.length ? IAM_TONE.granted : IAM_TONE.none)}>
                    {allowed.length}
                </Typography>
            </Typography>

            {allowed.length === 0 && (
                <Alert severity="warning" sx={{borderRadius: '14px'}}>
                    <FormattedMessage
                        id="accessControlNothingAllowed"
                        defaultMessage="Nothing. Either no role is assigned, or everything it grants sits outside what this business is allowed to hand out."
                    />
                </Alert>
            )}

            <Stack spacing={2}>
                {byGroup.map(([subject, group]) => (
                    <Box key={subject} sx={cardSx}>
                        <Box sx={subjectBandSx}>
                            <Box sx={subjectBadgeSx}>
                                <CheckCircleOutline fontSize="small"/>
                            </Box>
                            <Typography sx={{fontWeight: 600, fontSize: '0.875rem'}}>
                                {intl.formatMessage({
                                    id: `permissionSubject.${subject}`,
                                    defaultMessage: subject,
                                })}
                            </Typography>
                            {canOverride && (
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: 'accessControlRemoveSubject',
                                        defaultMessage: 'Remove all of these',
                                    })}
                                >
                                    <IconButton
                                        size="small"
                                        sx={{ml: 'auto'}}
                                        onClick={() => removeWholeSubject(group)}
                                    >
                                        <CloseOutlined fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        <Box sx={{px: 2.5, py: 2, display: 'flex', flexWrap: 'wrap', gap: 1}}>
                            {group.map((decision) => (
                                <Tooltip
                                    key={decision.permission}
                                    title={intl.formatMessage(
                                        {
                                            id: REASON_LABELS[decision.reason] ?? 'accessControlReasonNoGrant',
                                            defaultMessage: decision.reason,
                                        },
                                        {role: decision.sourceRoleCode, group: decision.sourceGroupName}
                                    )}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={0.5}
                                        sx={pillSx(
                                            decision.reason === 'USER_ALLOW'
                                                ? IAM_TONE.granted
                                                : IAM_TONE.none
                                        )}
                                    >
                                        {decision.reason === 'USER_ALLOW' && (
                                            <AddCircleOutline sx={{fontSize: 14}}/>
                                        )}
                                        <span>{decision.permission.split('.')[1] ?? decision.permission}</span>
                                        {canOverride && (
                                            // The cross reads as "not for this person" where it sits,
                                            // which is the only place the choice between allowing and
                                            // refusing makes sense without being explained.
                                            <CloseOutlined
                                                sx={{fontSize: 14, cursor: 'pointer', ml: 0.25}}
                                                onClick={() =>
                                                    decision.reason === 'USER_ALLOW'
                                                        ? clearException(decision.permission)
                                                        : setException(decision.permission, 'DENY')
                                                }
                                            />
                                        )}
                                    </Stack>
                                </Tooltip>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Stack>

            {granting && (
                <PermissionPickerModal
                    catalog={catalog ?? []}
                    held={held}
                    title={intl.formatMessage({
                        id: 'accessControlGrantExtra',
                        defaultMessage: 'Give an extra permission',
                    })}
                    onClose={() => setGranting(false)}
                    saving={savingGrant}
                    onAdd={async (names) => {
                        const chosen = new Set(names);
                        const granted = names.map((name) => ({
                            permissionName: name,
                            effect: 'ALLOW' as const,
                        }));
                        if (await writeExceptions([...without(chosen), ...granted])) {
                            setGranting(false);
                        }
                        // Left open on failure, as on the role page: the ticks are still there to
                        // retry with.
                    }}
                />
            )}
        </Box>
    );
};
